import { decodeThriftStruct, encodeThriftStruct, ThriftField, ThriftNode, ThriftStruct } from './thriftCompact';

export type ParquetColumnRenameErrorCode =
  | 'not_parquet'
  | 'encrypted_footer_unsupported'
  | 'nested_schema_unsupported'
  | 'duplicate_file_column'
  | 'duplicate_raw_name'
  | 'mapping_column_missing_in_file'
  | 'unmapped_file_column'
  | 'invalid_output_name'
  | 'duplicate_output_name'
  | 'invalid_pandas_metadata'
  | 'range_not_supported'
  | 'source_unavailable';

export class ParquetColumnRenameError extends Error {
  readonly code: ParquetColumnRenameErrorCode;

  constructor(code: ParquetColumnRenameErrorCode, message: string) {
    super(message);
    this.name = 'ParquetColumnRenameError';
    this.code = code;
  }
}

export interface ParquetColumnRename {
  rawName: string;
  outputName: string | null;
}

const FILE_METADATA_SCHEMA = 2;
const FILE_METADATA_ROW_GROUPS = 4;
const FILE_METADATA_KEY_VALUES = 5;
const FILE_METADATA_ENCRYPTION_ALGORITHM = 8;
const FILE_METADATA_FOOTER_SIGNING_KEY = 9;
const SCHEMA_ELEMENT_NAME = 4;
const SCHEMA_ELEMENT_NUM_CHILDREN = 5;
const ROW_GROUP_COLUMNS = 1;
const COLUMN_CHUNK_META_DATA = 3;
const COLUMN_CHUNK_META_PATH_IN_SCHEMA = 3;
const KEY_VALUE_KEY = 1;
const KEY_VALUE_VALUE = 2;
const PANDAS_KEY = 'pandas';
const ARROW_SCHEMA_KEY = 'ARROW:schema';

const decoder = new TextDecoder('utf-8');
const encoder = new TextEncoder();

const getField = (struct: ThriftStruct, id: number): ThriftNode | undefined =>
  struct.fields.find((field) => field.id === id)?.node;

const getStructField = (struct: ThriftStruct, id: number): ThriftStruct | undefined => {
  const node = getField(struct, id);
  return node?.type === 'struct' ? node : undefined;
};

const getListField = (struct: ThriftStruct, id: number) => {
  const node = getField(struct, id);
  return node?.type === 'list' ? node : undefined;
};

const setField = (struct: ThriftStruct, id: number, node: ThriftNode) => {
  const field = struct.fields.find((candidate) => candidate.id === id);
  if (field) {
    field.node = node;
  }
};

const binaryText = (node: ThriftNode | undefined): string | undefined =>
  node?.type === 'binary' ? decoder.decode(node.bytes) : undefined;

const parseFooter = (footerBytes: Uint8Array): ThriftStruct => {
  try {
    return decodeThriftStruct(footerBytes);
  } catch {
    throw new ParquetColumnRenameError('not_parquet', 'The Parquet footer could not be read');
  }
};

const assertSupportedFooter = (footer: ThriftStruct) => {
  if (getField(footer, FILE_METADATA_ENCRYPTION_ALGORITHM) || getField(footer, FILE_METADATA_FOOTER_SIGNING_KEY)) {
    throw new ParquetColumnRenameError('encrypted_footer_unsupported', 'Encrypted Parquet files are not supported');
  }
};

const getFlatSchemaLeaves = (footer: ThriftStruct): ThriftStruct[] => {
  const schema = getListField(footer, FILE_METADATA_SCHEMA);
  if (!schema || schema.items.length === 0 || schema.items.some((item) => item.type !== 'struct')) {
    throw new ParquetColumnRenameError('not_parquet', 'The Parquet footer has no schema');
  }
  const [root, ...leaves] = schema.items as ThriftStruct[];
  const rootChildren = getField(root, SCHEMA_ELEMENT_NUM_CHILDREN);
  const isFlat =
    rootChildren?.type === 'i32' &&
    rootChildren.value === leaves.length &&
    leaves.every((leaf) => {
      const children = getField(leaf, SCHEMA_ELEMENT_NUM_CHILDREN);
      return !children || (children.type === 'i32' && children.value === 0);
    });
  if (!isFlat) {
    throw new ParquetColumnRenameError(
      'nested_schema_unsupported',
      'Only flat Parquet schemas can have their columns renamed'
    );
  }
  return leaves;
};

const getLeafNames = (leaves: ThriftStruct[]): string[] =>
  leaves.map((leaf) => binaryText(getField(leaf, SCHEMA_ELEMENT_NAME)) ?? '');

export const getFooterColumnNames = (footerBytes: Uint8Array): string[] => {
  const footer = parseFooter(footerBytes);
  assertSupportedFooter(footer);
  return getLeafNames(getFlatSchemaLeaves(footer));
};

const buildRenameMap = (fileColumnNames: string[], renames: ParquetColumnRename[]): Map<string, string> => {
  const outputByRaw = new Map<string, string>();
  renames.forEach(({ rawName, outputName }) => {
    if (outputByRaw.has(rawName)) {
      throw new ParquetColumnRenameError('duplicate_raw_name', `Column '${rawName}' is mapped more than once`);
    }
    if (outputName === null || outputName.trim() === '') {
      throw new ParquetColumnRenameError('invalid_output_name', `Column '${rawName}' has no resolved output name`);
    }
    outputByRaw.set(rawName, outputName);
  });
  const fileColumns = new Set(fileColumnNames);
  if (fileColumns.size !== fileColumnNames.length) {
    throw new ParquetColumnRenameError('duplicate_file_column', 'The Parquet file repeats a column name');
  }
  outputByRaw.forEach((_output, rawName) => {
    if (!fileColumns.has(rawName)) {
      throw new ParquetColumnRenameError(
        'mapping_column_missing_in_file',
        `Mapped column '${rawName}' is not in the Parquet file`
      );
    }
  });
  fileColumnNames.forEach((name) => {
    if (!outputByRaw.has(name)) {
      throw new ParquetColumnRenameError('unmapped_file_column', `Column '${name}' has no mapping`);
    }
  });
  const outputs = new Set<string>();
  outputByRaw.forEach((outputName) => {
    if (outputs.has(outputName)) {
      throw new ParquetColumnRenameError('duplicate_output_name', `Output name '${outputName}' is used twice`);
    }
    outputs.add(outputName);
  });
  return outputByRaw;
};

const renamePandasMetadata = (json: string, outputByRaw: Map<string, string>): string => {
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new ParquetColumnRenameError('invalid_pandas_metadata', 'The pandas metadata is not valid JSON');
  }
  if (!parsed || typeof parsed !== 'object') {
    throw new ParquetColumnRenameError('invalid_pandas_metadata', 'The pandas metadata is not an object');
  }
  let changed = false;
  const rename = (value: unknown) => {
    const output = typeof value === 'string' ? outputByRaw.get(value) : undefined;
    if (output === undefined || output === value) {
      return value;
    }
    changed = true;
    return output;
  };
  if (Array.isArray(parsed.index_columns)) {
    parsed.index_columns = parsed.index_columns.map(rename);
  }
  if (Array.isArray(parsed.columns)) {
    parsed.columns = parsed.columns.map((column) =>
      column && typeof column === 'object'
        ? { ...column, name: rename(column.name), field_name: rename(column.field_name) }
        : column
    );
  }
  return changed ? JSON.stringify(parsed) : json;
};

const textNode = (value: string): ThriftNode => ({ type: 'binary', bytes: encoder.encode(value) });

const rewriteKeyValues = (footer: ThriftStruct, outputByRaw: Map<string, string>) => {
  const keyValues = getListField(footer, FILE_METADATA_KEY_VALUES);
  if (!keyValues) {
    return;
  }
  const kept: ThriftNode[] = [];
  keyValues.items.forEach((item) => {
    if (item.type !== 'struct') {
      kept.push(item);
      return;
    }
    const key = binaryText(getField(item, KEY_VALUE_KEY));
    if (key === ARROW_SCHEMA_KEY) {
      return;
    }
    if (key === PANDAS_KEY) {
      const value = binaryText(getField(item, KEY_VALUE_VALUE));
      if (value !== undefined) {
        setField(item, KEY_VALUE_VALUE, textNode(renamePandasMetadata(value, outputByRaw)));
      }
    }
    kept.push(item);
  });
  if (kept.length === 0) {
    footer.fields = footer.fields.filter((field: ThriftField) => field.id !== FILE_METADATA_KEY_VALUES);
    return;
  }
  keyValues.items = kept;
};

export const renameFooterColumns = (footerBytes: Uint8Array, renames: ParquetColumnRename[]): Uint8Array => {
  const footer = parseFooter(footerBytes);
  assertSupportedFooter(footer);
  const leaves = getFlatSchemaLeaves(footer);
  const fileColumnNames = getLeafNames(leaves);
  const outputByRaw = buildRenameMap(fileColumnNames, renames);

  leaves.forEach((leaf, index) => {
    setField(leaf, SCHEMA_ELEMENT_NAME, textNode(outputByRaw.get(fileColumnNames[index]) as string));
  });

  const rowGroups = getListField(footer, FILE_METADATA_ROW_GROUPS);
  rowGroups?.items.forEach((rowGroup) => {
    const columns = rowGroup.type === 'struct' ? getListField(rowGroup, ROW_GROUP_COLUMNS) : undefined;
    columns?.items.forEach((column, columnIndex) => {
      const metaData = column.type === 'struct' ? getStructField(column, COLUMN_CHUNK_META_DATA) : undefined;
      const path = metaData ? getListField(metaData, COLUMN_CHUNK_META_PATH_IN_SCHEMA) : undefined;
      const rawName = fileColumnNames[columnIndex];
      if (!path || path.items.length !== 1 || binaryText(path.items[0]) !== rawName) {
        throw new ParquetColumnRenameError(
          'nested_schema_unsupported',
          `Column chunk ${columnIndex} does not match the file schema`
        );
      }
      path.items = [textNode(outputByRaw.get(rawName) as string)];
    });
  });

  rewriteKeyValues(footer, outputByRaw);
  return encodeThriftStruct(footer);
};
